import { useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { useParentChildren } from '@/hooks/use-parent-data';
import { OutstandingInvoicesBanner } from '@/components/shared/outstanding-invoices-banner';
import { useParentAnnouncements } from '@/hooks/use-announcements';
import { AnnouncementsBanner } from '@/components/shared/announcements-banner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/use-auth';
import { usePinGuard } from '@/hooks/use-pin-guard';
import { PinSetupModal } from '@/components/kid/pin-setup-modal';
import { MissionConfirmCard } from '@/components/kid/mission-confirm-card';
import { cn } from '@/lib/utils';

export default function ParentDashboard() {
  const { data: children, isLoading: childrenLoading } = useParentChildren();
  const { data: announcementsData, isLoading: announcementsLoading } = useParentAnnouncements();
  const navigate = useNavigate();

  const { user } = useAuth();
  const { hasPin, setPin, clearPin } = usePinGuard(user?.clerkUserId ?? null);

  const [showPinSetup, setShowPinSetup] = useState(false);

  function handleHandToChild(childId: string) {
    navigate(`/kid/${childId}`);
  }

  const pinIsSet = hasPin();
  const hasChildren = !childrenLoading && children && children.length > 0;
  // Adult students have only self-linked profiles — Kid Mode doesn't apply to them
  const hasKidModeChildren = hasChildren && children!.some(c => c.relationshipType !== 'self');

  return (
    <div className="space-y-6">
      {/* Outstanding invoices — overdue only, persistent */}
      <OutstandingInvoicesBanner />

      {/* Mission confirmations — only renders when a child has pending claims */}
      <MissionConfirmCard />

      {/* My Players — hero section */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between pb-3">
          <CardTitle className="text-base">My Players</CardTitle>
          {!childrenLoading && children && children.length > 1 && (
            <Button variant="ghost" size="sm" className="text-xs" onClick={() => navigate('/parent/children')}>
              View all
            </Button>
          )}
        </CardHeader>
        <CardContent className="space-y-3">
          {childrenLoading ? (
            <>
              {[1, 2].map(i => <Skeleton key={i} className="h-24 w-full" />)}
            </>
          ) : children && children.length > 0 ? (
            children.map(child => (
              <div
                key={child.id}
                className="rounded-xl border border-border bg-surface-raised overflow-hidden"
              >
                {/* Child info row — navigates to detail */}
                <button
                  onClick={() => navigate(`/parent/children/${child.id}`)}
                  className="w-full flex items-center gap-4 p-4 hover:bg-secondary transition-colors text-left"
                >
                  {/* Avatar */}
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-clay-400 to-clay-600 flex items-center justify-center text-white font-bold text-lg shrink-0">
                    {child.firstName[0]}{child.lastName[0]}
                  </div>

                  {/* Name + level */}
                  <div className="flex-1 min-w-0">
                    <p className="font-semibold text-foreground">
                      {child.firstName} {child.lastName}
                    </p>
                    <div className="flex items-center gap-2 mt-0.5">
                      {child.level && (
                        <Badge variant="outline" className="capitalize text-xs">
                          {child.level}
                        </Badge>
                      )}
                      {child.utr && (
                        <span className="text-xs text-muted-foreground font-medium">
                          UTR {child.utr}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="text-right shrink-0 space-y-0.5">
                    <div className="text-xs text-muted-foreground">
                      Attendance{' '}
                      <span className={cn(
                        'font-semibold',
                        child.attendancePercent >= 80 ? 'text-success' :
                        child.attendancePercent >= 50 ? 'text-warning' :
                        'text-destructive'
                      )}>
                        {child.attendancePercent}%
                      </span>
                    </div>
                    {child.lastEvalAvg != null && (
                      <div className="text-xs text-muted-foreground">
                        Last eval{' '}
                        <span className="font-semibold text-info">{child.lastEvalAvg}/4</span>
                      </div>
                    )}
                  </div>

                  <ChevronRight className="w-4 h-4 text-muted-foreground shrink-0" />
                </button>

                {/* Kid Mode row — shown when PIN is set, not shown for adult self-profiles */}
                {pinIsSet && child.relationshipType !== 'self' && (
                  <div className="border-t border-border px-4 py-2.5 bg-card">
                    <button
                      onClick={() => handleHandToChild(child.id)}
                      className="w-full flex items-center justify-between text-sm font-medium text-primary hover:text-primary/80 transition-colors"
                    >
                      <span>🎾 Hand to {child.firstName}</span>
                      <span className="text-xs text-muted-foreground">Kid Mode →</span>
                    </button>
                  </div>
                )}
              </div>
            ))
          ) : (
            <div className="flex flex-col items-center justify-center py-8 text-center">
              <p className="text-sm font-medium text-foreground mb-1">No players linked yet</p>
              <p className="text-xs text-muted-foreground">
                Contact your club to get started.
              </p>
            </div>
          )}

          {/* Set PIN prompt — only shown when there are actual children (not adult self-profiles) */}
          {hasKidModeChildren && !pinIsSet && (
            <div className="rounded-xl border border-border p-4 flex items-center justify-between gap-3 bg-surface-raised">
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">Enable Kid Mode</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Set a PIN to hand your phone to your player
                </p>
              </div>
              <Button size="sm" onClick={() => setShowPinSetup(true)} className="shrink-0">
                Set PIN
              </Button>
            </div>
          )}

          {/* Reset PIN — shown when PIN is already set and there are real children */}
          {hasKidModeChildren && pinIsSet && (
            <button
              onClick={() => clearPin()}
              className="w-full text-xs text-muted-foreground hover:text-foreground transition-colors py-1"
            >
              Reset Kid Mode PIN
            </button>
          )}
        </CardContent>
      </Card>

      {/* Announcements — below the fold, toned down */}
      {(announcementsLoading || (announcementsData?.announcements?.length ?? 0) > 0) && (
        <AnnouncementsBanner
          announcements={announcementsData?.announcements ?? []}
          isLoading={announcementsLoading}
          messagesLink="/parent/messages"
          variant="parent"
        />
      )}

      {/* PIN setup modal */}
      <PinSetupModal
        open={showPinSetup}
        onSetPin={setPin}
        onComplete={() => setShowPinSetup(false)}
      />
    </div>
  );
}
